import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { images } from "../../constants/images";
import { navOptions } from "../../constants/navigation";
import styles from "./navBar.module.scss";
import Notifications from "../../containers/Notifications/NotificationsContainer";
import { NavBarProps } from "../../interfaces/components";
import Dropdown from "../Dropdown";
import { IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { FaRegBell, FaBell } from 'react-icons/fa';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useWindowSize } from "use-hooks";
import { tabs } from "../../constants/sidebar";
import Badge from '@mui/material-next/Badge';
import { googleLogout } from '@react-oauth/google';
import { motion } from "framer-motion";
import { trackEvent } from "../../config/firebase";
import { useAuthModal } from "../../context/AuthModalContext";
import { cleanFiltersAll } from "../../actions/filters";
import ButtonShared from "../Button";

const NavBar = ({
    user,
    notifications,
    logoutUser,
    cleanAllBuy,
    cleanExternalUser,
    cleanFiltersReducer,
    openDownloadModalTop,
    setSearchHomeReducer,
    setSearchReducer,
    cleanAgentConversationReducer
}: NavBarProps) => {
    const location = useLocation();
    const { hash, pathname, search } = location;
    const navigate = useNavigate();
    const windowSize = useWindowSize();
    const [openMenu, setOpenMenu] = useState(false);
    const [openNotifications, setOpenNotifications] = useState(false);
    const [openProfile, setOpenProfile] = useState(false);
    const [currentRoute, setCurrentRoute] = useState("home");
    const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
    const { handleOpenAuthModal: openAuthModalFromContext } = useAuthModal();

    useEffect(() => {
        setCurrentRoute(location.pathname)
    }, [location])

    const handleLogout = () => {
        cleanAllBuy();
        cleanExternalUser();
        googleLogout();
        logoutUser();
        navigate("/");
        setOpenMenu(false);
    };

    const onNavigateSell = () => {
        // navigate('/sell');
        setOpenMenu(false);
    };

    const onPressNotifications = () => {
        setOpenNotifications(prev => !prev);
        setOpenMenu(false);
    };

    const onPressProfile = () => {
        setOpenProfile(prev => !prev);
    };

    const onPressTab = (tab: any) => {
        navigate(`/perfil/${tab?.navigate}`);
        setOpenMenu(false);
    };

    const onPressLink = () => {
        setOpenMenu(false);
    };

    const onPressSale = () => {
        setOpenMenu(false);
        trackEvent("click-vender");
    };

    const onPressBuy = async () => {
        setOpenMenu(false);
        trackEvent("click-comprar");

        // Limpiar todos los filtros
        cleanFiltersReducer();
        cleanFiltersAll();
        cleanAgentConversationReducer();
        setSearchReducer("");
        setSearchHomeReducer("");

        // Esperar un momento para que Redux actualice el estado
        await new Promise(resolve => setTimeout(resolve, 50));

        // Navegar con state indicando que es una navegación limpia
        navigate("/buscar/autos/usados", {
            state: { cleanFilters: true },
            replace: true
        });
    };

    const handleOpenAuthModal = () => {
        openAuthModalFromContext();
        setOpenMenu(false);
    };

    const isMobile = windowSize.width <= 1024;

    const handleMobileMenuClose = () => {
        setOpenMenu(false);
        setMobileUserMenuOpen(false);
    };

    const getUserFirstName = () => {
        if (!user?.name) return 'Usuario';
        return user.name.split(' ')[0];
    };

    const handleMobileProfileAction = (path: string) => {
        navigate(path);
        handleMobileMenuClose();
    };

    return (
        <header className={`${pathname === '/' ? styles.navBarContainerHome : styles.navBarContainer} ${pathname === '/' ? styles.transparent : ''}`}>
            <NavLink className={styles.linzeLink} to={"/"}>
                <img src={images.isoLogoTipo} className={styles.logo} alt="Logo" title="Logo" />
            </NavLink>
            <IconButton className={styles.menu} onClick={() => setOpenMenu(true)}>
                <MenuIcon className={`${styles.icon}`} />
            </IconButton>
            <ul id="navBarOptions" className={`${styles.navBarLabels} ${openMenu && styles.open}`}>
                {/* Mobile login button - only visible when not logged in */}
                {isMobile && !user && (
                    <li className={styles.mobileLoginWrapper}>
                        <ButtonShared
                            onPress={handleOpenAuthModal}
                            title="Ingresar"
                            primary
                            size="small"
                            className={styles.mobileLoginBtn}
                        />
                    </li>
                )}
                {/* Mobile user header - only visible in responsive */}
                {isMobile && user && (
                    <li className={styles.mobileUserHeader}>
                        <div className={styles.mobileUserHeaderTop} onClick={() => setMobileUserMenuOpen(prev => !prev)}>
                            <div className={styles.mobileUserInfo}>
                                <div className={styles.mobileAvatar}>
                                    <PersonOutlineIcon className={styles.mobileAvatarIcon} />
                                </div>
                                <span className={styles.mobileUserName}>Hola, {getUserFirstName()}</span>
                            </div>
                            {mobileUserMenuOpen
                                ? <KeyboardArrowUpIcon className={styles.mobileChevron} />
                                : <KeyboardArrowDownIcon className={styles.mobileChevron} />
                            }
                        </div>
                        {mobileUserMenuOpen && (
                            <div className={styles.mobileUserDropdown}>
                                <div className={styles.mobileUserDropdownItem} onClick={() => handleMobileProfileAction('/perfil/cuenta')}>
                                    <PersonOutlineIcon className={styles.mobileDropdownIcon} />
                                    <span>Mi cuenta</span>
                                </div>
                                <div className={styles.mobileUserDropdownItem} onClick={() => handleMobileProfileAction('/perfil/mis_publicaciones')}>
                                    <LocalOfferOutlinedIcon className={styles.mobileDropdownIcon} />
                                    <span>Mis publicaciones</span>
                                </div>
                                <div className={styles.mobileUserDropdownItem} onClick={() => handleMobileProfileAction('/perfil/mis_compras')}>
                                    <DirectionsCarOutlinedIcon className={styles.mobileDropdownIcon} />
                                    <span>Mi actividad</span>
                                </div>
                                <div className={styles.mobileUserDropdownItem} onClick={() => handleMobileProfileAction('/perfil/favoritos')}>
                                    <FavoriteBorderIcon className={styles.mobileDropdownIcon} />
                                    <span>Favoritos</span>
                                </div>
                                <div className={`${styles.mobileUserDropdownItem} ${styles.mobileLogout}`} onClick={() => { handleLogout(); handleMobileMenuClose(); }}>
                                    <ExitToAppIcon className={styles.mobileDropdownIcon} />
                                    <span>Cerrar Sesión</span>
                                </div>
                            </div>
                        )}
                    </li>
                )}
                {
                    navOptions.map((option: any, index: number) => {
                        if (((option.to === "/perfil" || option.to === "/notificaciones" || option.to === "/notifications") && !user) || ((option.to === "/ingresar" || option.to === "/registro") && user)) return;
                        // Hide notifications and profile in mobile — handled by mobile user header
                        if (isMobile && option.to === "/notifications") return null;
                        if (isMobile && option.to === "/perfil" && user) return null;
                        if (isMobile && option.to === "/ingresar") return null;
                        if (option.to === "/notifications" && windowSize.width > 1024) {
                            let unreaded = 0;
                            return (
                                <li className={styles.notifications} key={index}>
                                    {
                                        notifications && notifications.forEach((item: any) => unreaded = (!item?.read) ? unreaded + 1 : unreaded)
                                    }
                                    {
                                        unreaded > 0 &&
                                        <Badge
                                            badgeContent={unreaded}
                                            color="error"
                                            classes={{
                                                root: styles.unReaded,
                                                badge: styles.badge
                                            }}
                                        />
                                    }
                                    <Dropdown
                                        type="icon"
                                        icon={openNotifications ? <FaBell className={`${styles.notificationsIcon}`} /> : <FaRegBell className={`${styles.notificationsIcon}`} />}
                                        onPress={onPressNotifications}
                                        className={styles.dropdown}
                                        id={`link-${option.id}`}
                                    >
                                        <Notifications user={user} />
                                    </Dropdown>
                                </li>
                            )
                        }
                        if (option.to === "/perfil" && user) {
                            return (
                                <li className={styles.profile} key={index}>
                                    <div className={`${styles.picture} ${openProfile && styles.open}`}>
                                        <img src={user?.picture ?? images.profile} className={styles.image} alt="user" title="user" onClick={() => navigate("/perfil")} />
                                        <Dropdown
                                            icon={
                                                <ArrowDropDownIcon className={`${styles.icon}`} />
                                            }
                                            onPress={onPressProfile}
                                            className={styles.dropdown}
                                            id={`link-${option.id}`}
                                        >
                                            <div className={styles.content}>
                                                {
                                                    tabs && tabs.map((tab: any, index: number) => (
                                                        <div key={index} className={styles.tab} onClick={() => onPressTab(tab)}>
                                                            <p>{tab?.label}</p>
                                                        </div>
                                                    ))
                                                }
                                                <div className={`${styles.tab} ${styles.blue}`} onClick={() => handleLogout()}>
                                                    <p>Cerrar sesión</p>
                                                </div>
                                            </div>
                                        </Dropdown>
                                    </div>
                                </li>
                            )
                        }
                        if (option.to) {
                            const isExternalLink = option.to.startsWith('http');

                            if (isExternalLink) {
                                return (
                                    <li className={styles.liLink} key={index}>
                                        <a
                                            href={option.to}
                                            target=""
                                            rel="noopener noreferrer"
                                            className={`${styles.link}`}
                                            id={`link-${option.id}`}
                                            title={option?.title}
                                        >
                                            <span className={styles.linkText}>
                                                <span className={styles.linkTitle}>
                                                    {option.title}
                                                </span>
                                                {option.badge && (
                                                    <span className={styles.newBadge}>{option.badge}</span>
                                                )}
                                            </span>
                                        </a>
                                    </li>
                                )
                            }

                            if (option.to === '/ingresar') {
                                return (
                                    <li className={`${styles.liLink} ${styles.border}`} key={index}>
                                        <button
                                            className={styles.link}
                                            id={`link-${option.id}`}
                                            title={option?.title}
                                            onClick={handleOpenAuthModal}
                                        >
                                            <span className={styles.linkText}>
                                                <span className={styles.linkTitle}>
                                                    {option.title}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            }

                            return (
                                <li className={`${styles.liLink}`} key={index}>
                                    <NavLink
                                        key={option.title}
                                        className={`
                                        ${styles.link}
                                        ${option.id === 'servicios' ? styles.serviciosLink : ''}
                                        ${option.id === 'financiar' ? styles.financiarLink : ''}
                                        ${({ isActive }: any) => isActive ? styles.activeLink : undefined}
                                    `}
                                        id={`link-${option.id}`}
                                        to={option.to}
                                        title={option?.title}
                                        onClick={option?.id === "sell" ? onPressSale : option?.id === "autos" ? onPressBuy : onPressLink}
                                    >
                                        <span className={styles.linkText}>
                                            <span className={styles.linkTitle}>
                                                {option.title}
                                                {(currentRoute === option.realPath ||
                                                    (Array.isArray(option.realPath) &&
                                                        option.realPath.some((path: string) => currentRoute.startsWith(path))) ||
                                                    (!Array.isArray(option.realPath) && currentRoute.startsWith(option.realPath)) ||
                                                    (!Array.isArray(option.realPath) && currentRoute === `${option.realPath}/`)) ?
                                                    <motion.div
                                                        className={styles.currentRoutePoint}
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{
                                                            duration: 0.6,
                                                            ease: "easeOut",
                                                            opacity: { duration: 0.4 },
                                                            scale: {
                                                                type: "spring",
                                                                stiffness: 500,
                                                                damping: 20,
                                                            },
                                                        }}
                                                    /> : null
                                                }
                                            </span>
                                            {option.badge && (
                                                <span className={styles.newBadge}>{option.badge}</span>
                                            )}
                                        </span>
                                    </NavLink>
                                </li>
                            )
                        }
                    })
                }
            </ul>
            <div className={`${styles.bkgr} ${openMenu && styles.open}`} onClick={handleMobileMenuClose}></div>
        </header>
    )
}
export default NavBar;